import { buildQueryString, request, requestWithHeaders, serializeCsvQuery } from "../../client"
import {
  archivePropertyUnitsBatch,
  createPropertyUnit,
  fetchPropertyUnits,
  restorePropertyUnitsBatch,
  updatePropertyUnit,
} from "../../unit-registry/property-units"
import {
  archiveRegistryRoots,
  restoreRegistryRoots,
  type RegistryArchiveBatchResponse,
} from "../../archive"

export type PropertyListResponse = {
  items: Property[]
  total: number
}

export type PropertyTableProjection = PropertyListResponse & {
  customers: Customer[]
  property_customers: PropertyCustomer[]
  contact_points: ContactPoint[]
}

export type PropertyDetailProjection = {
  property: Property
  customers: Customer[]
  property_customers: PropertyCustomer[]
  contact_points: ContactPoint[]
}

export type { RegistryArchiveBatchResponse } from "../../archive"

const stripPropertyWritePayload = (payload: PropertyCreatePayload | PropertyUpdatePayload) => {
  const { units, primary_contact_customer_id, ...propertyPayload } =
    payload as PropertyCreatePayload & {
      primary_contact_customer_id?: string | null
    }

  return {
    propertyPayload,
    units,
    primaryContactCustomerId: primary_contact_customer_id,
  }
}

const syncPropertyUnits = async (
  propertyId: string,
  units: PropertyUnitUpsertPayload[] | undefined | null,
  organizationId?: string,
): Promise<PropertyUnit[]> => {
  if (!units) {
    const rows = await fetchPropertyUnits({ propertyId, organizationId })

    return Array.isArray(rows) ? rows : []
  }

  const existing = await fetchPropertyUnits({
    propertyId,
    organizationId,
    archiveScope: "ALL",
  })
  const seenIds = new Set<string>()

  for (const unit of units) {
    if (unit.id) {
      seenIds.add(unit.id)
      const existingUnit = existing.find((entry) => entry.id === unit.id)

      if (existingUnit?.archived_at) {
        await restorePropertyUnitsBatch([unit.id])
      }

      await updatePropertyUnit(unit.id, {
        unit_label: unit.unit_label,
        unit_number: unit.unit_number,
        notes: unit.notes,
      })
      continue
    }

    const existingBuildingUnit =
      unit.unit_type === "BUILDING"
        ? existing.find((entry) => entry.unit_type === "BUILDING")
        : null

    if (existingBuildingUnit?.id) {
      seenIds.add(existingBuildingUnit.id)

      if (existingBuildingUnit.archived_at) {
        await restorePropertyUnitsBatch([existingBuildingUnit.id])
      }

      await updatePropertyUnit(existingBuildingUnit.id, {
        unit_label: unit.unit_label,
        unit_number: unit.unit_number,
        notes: unit.notes,
      })
      continue
    }

    const created = await createPropertyUnit({
      property_id: propertyId,
      unit_label: unit.unit_label,
      unit_type: unit.unit_type,
      unit_number: unit.unit_number,
      notes: unit.notes,
    })

    if (created.id) seenIds.add(created.id)
  }

  const removedUnitIds = existing
    .filter((unit) => unit.unit_type !== "BUILDING" && !seenIds.has(unit.id) && !unit.archived_at)
    .map((unit) => unit.id)

  if (removedUnitIds.length > 0) await archivePropertyUnitsBatch(removedUnitIds)

  const rows = await fetchPropertyUnits({ propertyId, organizationId })

  return Array.isArray(rows) ? rows : []
}

export const fetchProperties = async (query?: PropertyQuery) => {
  return request<Property[]>(
    `/property-registry/properties${buildQueryString({
      organization_id: query?.organizationId,
      property_ids: serializeCsvQuery(query?.propertyIds),
      search: query?.search,
      customer_query: query?.customerQuery,
      customer_id: query?.customerId,
      unit_query: query?.unitQuery,
      unit_count: query?.unitCount,
      contact_count: query?.contactCount,
      archive_scope: query?.archiveScope,
      offset: query?.offset,
      limit: query?.limit,
    })}`,
  )
}

export const fetchPropertiesWithTotal = async (
  query?: PropertyQuery,
): Promise<PropertyListResponse> => {
  const { data, headers } = await requestWithHeaders<Property[]>(
    `/property-registry/properties${buildQueryString({
      organization_id: query?.organizationId,
      property_ids: serializeCsvQuery(query?.propertyIds),
      search: query?.search,
      customer_query: query?.customerQuery,
      customer_id: query?.customerId,
      unit_query: query?.unitQuery,
      unit_count: query?.unitCount,
      contact_count: query?.contactCount,
      archive_scope: query?.archiveScope,
      offset: query?.offset,
      limit: query?.limit,
    })}`,
  )
  const rawTotal = headers.get("x-total-count")
  const total = rawTotal ? Number(rawTotal) : data.length

  return {
    items: data,
    total: Number.isFinite(total) ? total : data.length,
  }
}

export const fetchPropertyTable = (query?: PropertyQuery) =>
  request<PropertyTableProjection>(
    `/property-registry/properties${buildQueryString({
      projection: "table",
      organization_id: query?.organizationId,
      property_ids: serializeCsvQuery(query?.propertyIds),
      search: query?.search,
      customer_query: query?.customerQuery,
      customer_id: query?.customerId,
      unit_query: query?.unitQuery,
      unit_count: query?.unitCount,
      contact_count: query?.contactCount,
      archive_scope: query?.archiveScope,
      offset: query?.offset,
      limit: query?.limit,
    })}`,
  )

export const createProperty = async (payload: PropertyCreatePayload) => {
  const { propertyPayload, units } = stripPropertyWritePayload(payload)
  const created = await request<Property>("/property-registry/properties", {
    method: "POST",
    body: JSON.stringify(propertyPayload),
  })

  if (units === undefined || !created.id) return created

  const syncedUnits = await syncPropertyUnits(
    created.id,
    units,
    payload.organization_id ?? undefined,
  )

  return { ...created, units: syncedUnits }
}

export type PropertyBatchResponse = {
  properties: Property[]
  created_ids: string[]
  existing_ids: string[]
  skipped_duplicates: number
}

export type PropertyResolvePayload = Partial<PropertyCreatePayload> & {
  street: string
  organization_id?: string | null
  units?: PropertyUnitUpsertPayload[]
}

export type PropertyResolveUnit = {
  id: string
  property_id: string
  unit_label: string
  unit_type: PropertyUnit["unit_type"]
  unit_number?: string | null
}

export type PropertyResolveProperty = {
  id: string
  organization_id: string
  public_code?: string | null
  street: string
  house_number?: string | null
  zip?: string | null
  city?: string | null
  country?: string | null
  address_extra?: string | null
  geo_lat?: number | null
  geo_lng?: number | null
  units: PropertyResolveUnit[]
}

export type PropertyResolveCandidate = {
  key: string
  source_index: number
  payload: PropertyResolvePayload
  property_id: string | null
  status: "matched" | "missing" | string
  display_label: string
}

export type PropertyResolveBatchResponse = {
  candidates: PropertyResolveCandidate[]
  properties: PropertyResolveProperty[]
  selected_property_ids: string[]
  summary: {
    input_count: number
    unique_count: number
    matched_count: number
    missing_count: number
  }
}

export const propertyFromResolveProperty = (property: PropertyResolveProperty): Property => ({
  id: property.id,
  organization_id: property.organization_id,
  public_code: property.public_code ?? null,
  street: property.street,
  house_number: property.house_number ?? null,
  zip: property.zip ?? null,
  city: property.city ?? null,
  country: property.country ?? null,
  address_extra: property.address_extra ?? null,
  geo_lat: property.geo_lat ?? null,
  geo_lng: property.geo_lng ?? null,
  primary_contact_customer_id: null,
  archived_at: null,
  archive_reason: null,
  units: (property.units ?? []).map((unit) => ({
    id: unit.id,
    organization_id: property.organization_id,
    property_id: unit.property_id,
    unit_label: unit.unit_label,
    unit_type: unit.unit_type,
    unit_number: unit.unit_number ?? null,
    archived_at: null,
    archive_reason: null,
    effective_archived_at: null,
    effective_archive_reason: null,
    archive_source: null,
    notes: null,
    created_at: null,
  })),
  created_at: null,
  updated_at: null,
})

const buildPropertyKey = (value: Partial<PropertyCreatePayload> | Partial<Property>) =>
  [
    value.street ?? "",
    value.house_number ?? "",
    value.zip ?? "",
    value.city ?? "",
    value.country ?? "",
    value.address_extra ?? "",
  ]
    .join("|")
    .toLowerCase()

export const createPropertiesBatch = async (payload: PropertyCreatePayload[]) => {
  const strippedPayload = payload.map((item) => stripPropertyWritePayload(item).propertyPayload)
  const response = await request<PropertyBatchResponse>("/property-registry/properties/batch", {
    method: "POST",
    body: JSON.stringify(strippedPayload),
  })

  if (!Array.isArray(response?.properties)) return response

  const sourceByKey = new Map(payload.map((item) => [buildPropertyKey(item), item]))
  const properties = await Promise.all(
    response.properties.map(async (property) => {
      const source = sourceByKey.get(buildPropertyKey(property))

      if (!source?.units) return property

      const units = await syncPropertyUnits(
        property.id,
        source?.units,
        source?.organization_id ?? property.organization_id,
      )

      return { ...property, units }
    }),
  )

  return { ...response, properties }
}

export const resolvePropertiesBatch = async (payload: PropertyResolvePayload[]) => {
  return request<PropertyResolveBatchResponse>("/property-registry/properties/resolve-batch", {
    method: "POST",
    body: JSON.stringify(payload),
  })
}

export const updateProperty = async (propertyId: string, payload: PropertyUpdatePayload) => {
  const { propertyPayload, units } = stripPropertyWritePayload(payload)
  const updated = await request<Property>(`/property-registry/properties/${propertyId}`, {
    method: "PATCH",
    body: JSON.stringify(propertyPayload),
  })

  if (units === undefined) return updated

  const syncedUnits = await syncPropertyUnits(
    propertyId,
    units,
    payload.organization_id ?? undefined,
  )

  return { ...updated, units: syncedUnits }
}

export const archivePropertiesBatch = (
  propertyIds: string[],
  reason: ArchiveReason = "USER_REQUEST",
): Promise<RegistryArchiveBatchResponse> =>
  archiveRegistryRoots(
    "/property-registry/properties/archive-batch",
    "property",
    propertyIds,
    reason,
  )

export const restorePropertiesBatch = (
  propertyIds: string[],
): Promise<RegistryArchiveBatchResponse> =>
  restoreRegistryRoots("/property-registry/properties/restore-batch", "property", propertyIds)

export const fetchPropertyDetailProjection = (propertyId: string) =>
  request<PropertyDetailProjection>(
    `/property-registry/properties/${propertyId}${buildQueryString({
      projection: "detail",
    })}`,
  )

export const geocodeProperty = (payload: PropertyGeocodePayload) =>
  request<PropertyGeocodeResponse>("/property-registry/properties/geocode", {
    method: "POST",
    body: JSON.stringify(payload),
  })
